// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { AzureClouds, AzureSupportedClouds } from "./static-helpers/cloudSettingHelpers.js";
import {
  PageSettings,
  ContinuablePage,
  PagedAsyncIterableIterator,
} from "./static-helpers/pagingHelpers.js";

export { RecoveryServicesClient } from "./recoveryServicesClient.js";
export { restorePoller, RestorePollerOptions } from "./restorePollerHelpers.js";
export {
  OperationResource,
  ErrorModel,
  ErrorAdditionalInfo,
  CloudError,
  Vault,
  VaultProperties,
  UpgradeDetails,
  KnownVaultUpgradeState,
  VaultUpgradeState,
  KnownTriggerType,
  TriggerType,
  PrivateEndpointConnectionVaultProperties,
  PrivateEndpointConnection,
  KnownProvisioningState,
  ProvisioningState,
  PrivateEndpoint,
  PrivateLinkServiceConnectionState,
  KnownPrivateEndpointConnectionStatus,
  PrivateEndpointConnectionStatus,
  KnownVaultSubResourceType,
  VaultSubResourceType,
  KnownVaultPrivateEndpointState,
  VaultPrivateEndpointState,
  VaultPropertiesEncryption,
  CmkKeyVaultProperties,
  CmkKekIdentity,
  KnownInfrastructureEncryptionState,
  InfrastructureEncryptionState,
  VaultPropertiesMoveDetails,
  KnownResourceMoveState,
  ResourceMoveState,
  KnownBackupStorageVersion,
  BackupStorageVersion,
  KnownPublicNetworkAccess,
  PublicNetworkAccess,
  MonitoringSettings,
  AzureMonitorAlertSettings,
  KnownAlertsState,
  AlertsState,
  ClassicAlertSettings,
  RestoreSettings,
  CrossSubscriptionRestoreSettings,
  KnownCrossSubscriptionRestoreState,
  CrossSubscriptionRestoreState,
  VaultPropertiesRedundancySettings,
  KnownStandardTierStorageRedundancy,
  StandardTierStorageRedundancy,
  KnownCrossRegionRestore,
  CrossRegionRestore,
  SecuritySettings,
  ImmutabilitySettings,
  KnownImmutabilityState,
  ImmutabilityState,
  SoftDeleteSettings,
  KnownSoftDeleteState,
  SoftDeleteState,
  KnownEnhancedSecurityState,
  EnhancedSecurityState,
  KnownMultiUserAuthorization,
  MultiUserAuthorization,
  SourceScanConfiguration,
  KnownState,
  State,
  AssociatedIdentity,
  KnownIdentityType,
  IdentityType,
  KnownSecureScoreLevel,
  SecureScoreLevel,
  KnownBcdrSecurityLevel,
  BcdrSecurityLevel,
  IdentityData,
  KnownResourceIdentityType,
  ResourceIdentityType,
  UserIdentity,
  Sku,
  KnownSkuName,
  SkuName,
  TrackedResource,
  Resource,
  SystemData,
  KnownCreatedByType,
  CreatedByType,
  ClientDiscoveryValueForSingleApi,
  ClientDiscoveryDisplay,
  ClientDiscoveryForProperties,
  ClientDiscoveryForServiceSpecification,
  ClientDiscoveryForLogSpecification,
  ErrorDetail,
  PatchVault,
  PatchTrackedResource,
  ErrorResponse,
  DeletedVault,
  DeletedVaultProperties,
  ProxyResource,
  DeletedVaultUndeleteInput,
  DeletedVaultUndeleteInputProperties,
  PrivateLinkResource,
  PrivateLinkResourceProperties,
  CertificateRequest,
  RawCertificateData,
  KnownAuthType,
  AuthType,
  VaultCertificateResponse,
  ResourceCertificateDetails,
  ResourceCertificateDetailsUnion,
  ResourceCertificateAndAadDetails,
  ResourceCertificateAndAcsDetails,
  ReplicationUsage,
  MonitoringSummary,
  JobsSummary,
  VaultUsage,
  KnownUsagesUnit,
  UsagesUnit,
  NameInfo,
  VaultExtendedInfoResource,
  VaultExtendedInfo,
  CheckNameAvailabilityParameters,
  CheckNameAvailabilityResult,
  ResourceCapabilities,
  CapabilitiesProperties,
  DNSZone,
  ResourceCapabilitiesBase,
  CapabilitiesResponse,
  CapabilitiesResponseProperties,
  DNSZoneResponse,
  KnownVersions,
} from "./models/index.js";
export {
  GetOperationResultOptionalParams,
  GetOperationStatusOptionalParams,
  RecoveryServicesClientOptionalParams,
} from "./api/index.js";
export {
  DeletedVaultsGetOperationStatusOptionalParams,
  DeletedVaultsUndeleteOptionalParams,
  DeletedVaultsGetOptionalParams,
  DeletedVaultsListBySubscriptionIdOptionalParams,
} from "./api/deletedVaults/index.js";
export { OperationsListOptionalParams } from "./api/operations/index.js";
export {
  PrivateLinkResourcesListOptionalParams,
  PrivateLinkResourcesGetOptionalParams,
} from "./api/privateLinkResources/index.js";
export {
  RecoveryServicesCapabilitiesOptionalParams,
  RecoveryServicesCheckNameAvailabilityOptionalParams,
} from "./api/recoveryServices/index.js";
export { RegisteredIdentitiesDeleteOptionalParams } from "./api/registeredIdentities/index.js";
export { ReplicationUsagesListOptionalParams } from "./api/replicationUsages/index.js";
export { UsagesListByVaultsOptionalParams } from "./api/usages/index.js";
export { VaultCertificatesCreateOptionalParams } from "./api/vaultCertificates/index.js";
export {
  VaultExtendedInfoUpdateOptionalParams,
  VaultExtendedInfoCreateOrUpdateOptionalParams,
  VaultExtendedInfoGetOptionalParams,
} from "./api/vaultExtendedInfo/index.js";
export {
  VaultsListByResourceGroupOptionalParams,
  VaultsListBySubscriptionIdOptionalParams,
  VaultsDeleteOptionalParams,
  VaultsUpdateOptionalParams,
  VaultsCreateOrUpdateOptionalParams,
  VaultsGetOptionalParams,
} from "./api/vaults/index.js";
export {
  DeletedVaultsOperations,
  OperationsOperations,
  PrivateLinkResourcesOperations,
  RecoveryServicesOperations,
  RegisteredIdentitiesOperations,
  ReplicationUsagesOperations,
  UsagesOperations,
  VaultCertificatesOperations,
  VaultExtendedInfoOperations,
  VaultsOperations,
} from "./classic/index.js";
export { PageSettings, ContinuablePage, PagedAsyncIterableIterator };
export { AzureClouds, AzureSupportedClouds };
